import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, toast } from '../../App';
import { 
  Bot, RefreshCw, CheckCircle, XCircle, Clock, 
  FileText, Image, ChevronDown, ChevronUp, ExternalLink,
  Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';

const MCPImportsCard = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ total: 0, today: 0, articles: 0 });
  const [filter, setFilter] = useState('all'); // all, today, week

  const loadImports = useCallback(async () => {
    if (user?.grade !== 'amiral') return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/mcp-imports`);
      setImports(data.imports || []);
      setStats(data.stats || { total: 0, today: 0, articles: 0 });
    } catch (error) {
      console.error('Error loading MCP imports:', error);
      // Silently fail if endpoint doesn't exist yet
    } finally {
      setLoading(false);
    }
  }, [user?.grade]);

  useEffect(() => {
    if (isOpen) {
      loadImports();
    }
  }, [isOpen, loadImports]);

  // Ne pas afficher si pas amiral
  if (user?.grade !== 'amiral') {
    return null;
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-emerald-400" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-400" />;
      case 'processing':
        return <Clock className="w-4 h-4 text-amber-400 animate-pulse" />;
      default:
        return <Clock className="w-4 h-4 text-slate-400" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed': return 'Terminé';
      case 'failed': return 'Échoué';
      case 'processing': return 'En cours';
      default: return status;
    }
  };

  const filteredImports = imports.filter(imp => {
    if (filter === 'all') return true;
    const date = new Date(imp.created_at);
    const now = new Date();
    if (filter === 'today') {
      return date.toDateString() === now.toDateString();
    }
    if (filter === 'week') {
      const weekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
      return date >= weekAgo;
    }
    return true;
  });

  return (
    <Card className="bg-slate-900/50 border-slate-800" data-testid="mcp-imports-card">
      <CardHeader 
        className="cursor-pointer hover:bg-slate-800/30 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <CardTitle className="flex items-center justify-between text-cyan-400">
          <div className="flex items-center gap-2">
            <Bot className="w-5 h-5" />
            Imports MCP (ChatGPT)
            {stats.today > 0 && (
              <span className="text-xs bg-cyan-600/30 text-cyan-300 px-2 py-0.5 rounded-full">
                {stats.today} aujourd'hui
              </span>
            )}
          </div>
          {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </CardTitle>
      </CardHeader>
      
      {isOpen && (
        <CardContent className="space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-slate-800/50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-cyan-400">{stats.total}</div>
              <div className="text-xs text-slate-400">Imports totaux</div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-emerald-400">{stats.articles}</div>
              <div className="text-xs text-slate-400">Articles importés</div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-amber-400">{stats.today}</div>
              <div className="text-xs text-slate-400">Aujourd'hui</div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              <button
                onClick={() => setFilter('all')}
                className={`px-3 py-1 rounded-lg text-xs transition-colors ${
                  filter === 'all' 
                    ? 'bg-cyan-600/30 text-cyan-300' 
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                }`}
              >
                Tous
              </button>
              <button
                onClick={() => setFilter('today')}
                className={`px-3 py-1 rounded-lg text-xs transition-colors ${
                  filter === 'today' 
                    ? 'bg-cyan-600/30 text-cyan-300' 
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                }`}
              >
                Aujourd'hui
              </button>
              <button
                onClick={() => setFilter('week')}
                className={`px-3 py-1 rounded-lg text-xs transition-colors ${
                  filter === 'week' 
                    ? 'bg-cyan-600/30 text-cyan-300' 
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                }`}
              >
                7 jours
              </button>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={loadImports}
              disabled={loading}
              className="h-8"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {/* Imports list */}
          {loading ? (
            <div className="text-center text-slate-500 py-4">
              <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
              Chargement...
            </div>
          ) : filteredImports.length === 0 ? (
            <div className="text-center text-slate-500 py-6">
              <Bot className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>Aucun import MCP pour le moment</p>
              <p className="text-xs mt-1">Les imports via ChatGPT apparaîtront ici</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {filteredImports.map((imp, idx) => (
                <div 
                  key={imp.job_id || idx}
                  className="bg-slate-800/30 rounded-lg p-3 border border-slate-700/50"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {getStatusIcon(imp.status)}
                        <span className="text-sm font-medium text-slate-200">
                          {imp.site_name || 'Site inconnu'}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          imp.status === 'completed' 
                            ? 'bg-emerald-600/20 text-emerald-400'
                            : imp.status === 'failed'
                            ? 'bg-red-600/20 text-red-400'
                            : 'bg-amber-600/20 text-amber-400'
                        }`}>
                          {getStatusLabel(imp.status)}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-4 text-xs text-slate-400">
                        <span className="flex items-center gap-1">
                          <FileText className="w-3 h-3" />
                          {imp.imported || 0}/{imp.total || 0} articles
                        </span>
                        {imp.images > 0 && (
                          <span className="flex items-center gap-1">
                            <Image className="w-3 h-3" />
                            {imp.images} images
                          </span>
                        )}
                        <span>
                          {imp.published > 0 && `${imp.published} publiés`}
                          {imp.drafts > 0 && ` ${imp.drafts} brouillons`}
                        </span>
                      </div>
                      
                      {imp.errors?.length > 0 && (
                        <div className="mt-2 text-xs text-red-400">
                          ⚠️ {imp.errors.length} erreur(s)
                        </div>
                      )}
                    </div>
                    
                    <div className="text-xs text-slate-500">
                      {imp.created_at && new Date(imp.created_at).toLocaleString('fr-FR', {
                        day: '2-digit',
                        month: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* MCP Info */}
          <div className="text-xs text-slate-500 bg-slate-800/30 p-3 rounded-lg">
            <p className="flex items-center gap-1 text-slate-400 mb-1">
              <Bot className="w-3 h-3" /> Intégration MCP ChatGPT
            </p>
            <p>
              Connectez ChatGPT à Auto Edito pour importer des articles générés 
              directement dans vos sites WordPress.
            </p>
            <a 
              href="/api/mcp/" 
              target="_blank"
              className="inline-flex items-center gap-1 text-cyan-400 hover:text-cyan-300 mt-2"
            >
              Voir la documentation MCP <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default MCPImportsCard;
